import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  FileText,
  Loader2,
  MoreHorizontal,
} from 'lucide-react'
import type { Message } from '@/components/layout/chat-pane.types'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  buildLiveStatusScript,
  buildThinkingSteps,
  collectHardErrorBanner,
  extractThinkingDocuments,
  thinkingTimelineIsActive,
  type ThinkingStep,
} from '@/components/features/chat-messages/thinking-timeline.helpers'
import { LiveWaitStatus } from '@/components/features/chat-messages/live-wait-status'
import { useLiveWaitElapsed } from '@/hooks/use-live-wait-elapsed'

export interface ThinkingTimelineProps {
  messages: Message[]
  isStreaming?: boolean
  isLastTurnGroup?: boolean
  /** In-flight operation label. Arrives before the tool result. */
  liveLabel?: string | null
  onDocumentClick?: (docId: string, event?: MouseEvent) => void | Promise<void>
  activeDocumentId?: string | null
}

/**
 * Play status lines in order. A live turn starts at the first line so a burst
 * of reads still scrolls through each title. A settled turn opens on the summary.
 */
function useRevealedStatusLine(lines: string[], playFromStart: boolean): string {
  const [index, setIndex] = useState(() =>
    playFromStart ? 0 : Math.max(0, lines.length - 1),
  )

  useEffect(() => {
    const target = Math.max(0, lines.length - 1)
    if (index >= target) {
      if (index > target) setIndex(target)
      return
    }
    const behind = target - index
    const delay = behind === 1 ? 50 : behind > 3 ? 160 : 380
    const timer = window.setTimeout(() => setIndex((current) => current + 1), delay)
    return () => window.clearTimeout(timer)
  }, [index, lines])

  const safe = Math.min(index, Math.max(0, lines.length - 1))
  return lines[safe] || 'Thinking...'
}

/** One status line. The previous line leaves upward and the next enters from below. */
function SlidingStatusLine({ text }: { text: string }) {
  const previousText = useRef(text)
  const [leaving, setLeaving] = useState<string | null>(null)
  const reduceMotion = useRef(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    if (previousText.current === text) return
    const prior = previousText.current
    previousText.current = text
    if (reduceMotion.current) {
      setLeaving(null)
      return
    }
    setLeaving(prior)
    const timer = window.setTimeout(() => setLeaving(null), 240)
    return () => window.clearTimeout(timer)
  }, [text])

  return (
    <span className="relative block h-5 overflow-hidden leading-5" aria-live="polite">
      {leaving ? (
        <span className="chat-status-leave pointer-events-none absolute inset-x-0 top-0 block truncate">
          {leaving}
        </span>
      ) : null}
      <span key={text} className={reduceMotion.current ? 'block truncate' : 'chat-status-enter block truncate'}>
        {text}
      </span>
    </span>
  )
}

/** Renders expandable raw JSON for a timeline step. */
function StepDetailsPanel({ step }: { step: ThinkingStep }) {
  if (!step.toolResult) return null
  return (
    <pre className="mt-1 overflow-x-auto rounded-md bg-muted p-2 text-[10px] text-muted-foreground">
      {JSON.stringify(step.toolResult, null, 2)}
    </pre>
  )
}

/** Single quiet row in the expanded thinking scroll viewport. */
function StepRow({
  step,
  openDetailsId,
  onToggleDetails,
}: {
  step: ThinkingStep
  openDetailsId: string | null
  onToggleDetails: (id: string | null) => void
}) {
  return (
    <div
      className="group flex items-start gap-2 rounded-md px-1.5 py-1.5 hover:bg-muted/50"
      data-testid={testIds.chatPane.thinkingTimelineStep(step.id)}
    >
      <span className="mt-0.5 shrink-0">
        {step.isRunning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
        ) : step.isHardFailure ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500" aria-hidden />
        ) : step.isSoftFailure ? (
          <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
        ) : (
          <Check className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">{step.label}</div>
        {step.footnote ? (
          <p
            className={cn(
              'min-w-0 break-words text-xs leading-snug [overflow-wrap:anywhere]',
              step.isSoftFailure
                ? 'italic text-muted-foreground/80'
                : step.isHardFailure
                  ? 'text-amber-700/90 dark:text-amber-400/90'
                  : 'text-muted-foreground',
            )}
          >
            {step.footnote}
          </p>
        ) : null}
        {openDetailsId === step.id ? <StepDetailsPanel step={step} /> : null}
      </div>
      {step.showDetails ? (
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground opacity-70 hover:bg-muted hover:opacity-100"
          aria-label={`Details for ${step.label}`}
          data-testid={testIds.chatPane.thinkingTimelineStepDetails(step.id)}
          onClick={(e) => {
            e.stopPropagation()
            onToggleDetails(openDetailsId === step.id ? null : step.id)
          }}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}

/** One morphing Thinking surface per live assistant turn. */
export function ThinkingTimeline({
  messages,
  isStreaming = false,
  isLastTurnGroup = false,
  liveLabel = null,
  onDocumentClick,
  activeDocumentId,
}: ThinkingTimelineProps) {
  const [expanded, setExpanded] = useState(false)
  const [openDetailsId, setOpenDetailsId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const isActive = thinkingTimelineIsActive(messages, isStreaming, isLastTurnGroup)
  const waitElapsedMs = useLiveWaitElapsed(isActive)
  const statusLines = useMemo(
    () =>
      buildLiveStatusScript({
        messages,
        isActive,
        pendingLabel: isActive ? liveLabel : null,
      }),
    [messages, isActive, liveLabel],
  )
  const header = useRevealedStatusLine(statusLines, isActive)
  const steps = useMemo(() => buildThinkingSteps(messages), [messages])
  const documents = useMemo(() => extractThinkingDocuments(messages), [messages])
  const hardError = useMemo(() => collectHardErrorBanner(messages), [messages])
  const settled = !isActive

  useEffect(() => {
    if (!expanded || !scrollRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [expanded, steps.length, isActive])

  if (messages.length === 0) return null

  return (
    <div className="w-full min-w-0 space-y-2" data-testid={testIds.chatPane.thinkingTimeline}>
      <div className="overflow-hidden rounded-xl border border-border bg-muted/20">
        <button
          type="button"
          className="flex w-full items-start gap-2 px-3 py-2.5 text-left"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          data-testid={testIds.chatPane.thinkingTimelineHeader}
        >
          {isActive ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
          ) : (
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          )}
          <span className="min-w-0 flex-1 text-sm text-foreground">
            <SlidingStatusLine text={header} />
            {isActive ? <LiveWaitStatus elapsedMs={waitElapsedMs} /> : null}
          </span>
          <ChevronRight
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
            aria-hidden
          />
        </button>

        {expanded ? (
          <div className="border-t border-border">
            <div
              ref={scrollRef}
              className="max-h-[240px] space-y-1 overflow-y-auto px-2 py-2"
              data-testid={testIds.chatPane.thinkingTimelineScroll}
            >
              {steps.map((step) => (
                <StepRow
                  key={step.id}
                  step={step}
                  openDetailsId={openDetailsId}
                  onToggleDetails={setOpenDetailsId}
                />
              ))}

              {settled && documents.length > 0 ? (
                <div className="mt-1 rounded-md border border-border/60 bg-card/50">
                  <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground">
                    Found {documents.length} document{documents.length === 1 ? '' : 's'}
                  </div>
                  {documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-2 border-t border-border/60 px-2 py-1.5 text-left hover:bg-muted/40',
                        activeDocumentId === doc.id && 'bg-muted/50',
                      )}
                      data-testid={testIds.chatPane.thinkingTimelineDocRow(doc.id)}
                      onClick={(e) => {
                        void onDocumentClick?.(doc.id, e)
                      }}
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-foreground">
                          {doc.title}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {doc.path}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {hardError ? (
        <div
          className="flex min-w-0 items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-sm text-amber-800/90 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200/85"
          data-testid={testIds.chatPane.thinkingTimelineHardError}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600/80 dark:text-amber-400/80" aria-hidden />
          <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">{hardError}</span>
        </div>
      ) : null}
    </div>
  )
}
